import { createRow } from "../factories";
import { findGroup, locateRow } from "../find";
import { isGroup } from "../sections";
import { canDeleteRow } from "../row-policy";
import type { Row } from "../types";
import { applySelectionChange } from "./draft";
import type { EditorDraft } from "./types";
import { applySectionSelection } from "./shared";

function applyDefaultRowShape(row: Row, nestedInGroup: boolean): void {
  const rowId = row.id;
  const columnId = row.columns[0]?.id;
  const fresh = createRow("one", undefined, { nestedInGroup });
  Object.assign(row, fresh, { id: rowId });
  if (columnId !== undefined && row.columns[0]) {
    row.columns[0].id = columnId;
  }
}

export function clearRow(draft: EditorDraft, rowId: string): boolean {
  const located = locateRow(draft.document.sections, rowId);
  if (!located) {
    return false;
  }

  applyDefaultRowShape(
    located.row,
    located.location.parent === "group",
  );
  applySelectionChange(draft, { kind: "row", rowId });
  return true;
}

export function deleteRowOrClear(draft: EditorDraft, rowId: string): boolean {
  if (canDeleteRow(draft.document, rowId)) {
    return deleteRow(draft, rowId);
  }
  return clearRow(draft, rowId);
}

export function deleteRow(draft: EditorDraft, rowId: string): boolean {
  if (!canDeleteRow(draft.document, rowId)) {
    return false;
  }
  const located = locateRow(draft.document.sections, rowId);
  if (!located) {
    return false;
  }

  if (located.location.parent === 'document') {
    draft.document.sections.splice(located.location.sectionIndex, 1);
    const next =
      draft.document.sections[
        Math.min(
          located.location.sectionIndex,
          draft.document.sections.length - 1,
        )
      ];
    if (next) {
      applySectionSelection(draft, next);
    } else {
      draft.selection = { kind: 'email' };
    }
    return true;
  }

  const group = findGroup(draft.document.sections, located.location.groupId);
  if (!group) {
    return false;
  }
  group.rows.splice(located.location.rowIndex, 1);
  const nextRow =
    group.rows[located.location.rowIndex] ??
    group.rows[located.location.rowIndex - 1];
  if (nextRow) {
    draft.selection = { kind: 'row', rowId: nextRow.id };
  } else {
    draft.selection = { kind: 'group', groupId: group.id };
  }
  return true;
}

export function ungroup(draft: EditorDraft, groupId: string): boolean {
  const index = draft.document.sections.findIndex(
    section => section.id === groupId,
  );
  const section = draft.document.sections[index];
  if (!section || !isGroup(section)) {
    return false;
  }
  const rows = [...section.rows];
  draft.document.sections.splice(index, 1, ...rows);
  const first = rows[0];
  draft.selection = first
    ? { kind: 'row', rowId: first.id }
    : { kind: 'email' };
  return true;
}

export function deleteGroup(
  draft: EditorDraft,
  groupId: string,
  mode: 'delete' | 'ungroup',
): boolean {
  if (mode === 'ungroup') {
    return ungroup(draft, groupId);
  }
  if (draft.document.sections.length <= 1) {
    return false;
  }
  const index = draft.document.sections.findIndex(
    section => section.id === groupId,
  );
  if (index < 0) {
    return false;
  }
  draft.document.sections.splice(index, 1);
  const next =
    draft.document.sections[
      Math.min(index, draft.document.sections.length - 1)
    ];
  if (next) {
    applySectionSelection(draft, next);
  } else {
    draft.selection = { kind: 'email' };
  }
  return true;
}
